import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import {
  MagnifyingGlass, Funnel, CircleNotch, Globe, ShieldCheck, Heartbeat,
  Lightning, Key, CheckCircle, XCircle, Eye, Clock, Warning,
  ArrowRight, Play, Pause,
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  submitted: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Submitted', icon: Clock },
  in_review: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', label: 'In Review', icon: Eye },
  approved: { bg: 'bg-cyan-500/15', text: 'text-cyan-400', border: 'border-cyan-500/30', label: 'Approved', icon: CheckCircle },
  active: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Active', icon: CheckCircle },
  rejected: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Rejected', icon: XCircle },
  suspended: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', label: 'Suspended', icon: Warning },
};

const TRUST_OPTIONS = [
  { value: 'external_registered', label: 'Registered', desc: 'Standard external agent' },
  { value: 'verified_partner', label: 'Verified Partner', desc: 'Trusted partner agent' },
  { value: 'internal_trusted', label: 'Internal Trusted', desc: 'Internal team agent' },
  { value: 'unverified_remote', label: 'Unverified', desc: 'Minimal trust' },
];

const HEALTH_STYLES = {
  healthy: 'text-emerald-400', degraded: 'text-yellow-400',
  unreachable: 'text-red-400', unknown: 'text-[#718096]',
};

const AUTH_LABELS = { api_key: 'API Key', oauth: 'OAuth', none: 'None', bearer: 'Bearer', hmac: 'HMAC' };

function ExternalAgentReview() {
  const [queue, setQueue] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchQueue = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/queue?limit=100`);
      if (res.ok) { const data = await res.json(); setQueue(data.items || []); }
    } catch {}
  }, []);

  const fetchAllAgents = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/list?limit=200`);
      if (res.ok) {
        const data = await res.json();
        const items = data.items || data.federations || data;
        setAllAgents(Array.isArray(items) ? items : []);
      }
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/queue/stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const refresh = useCallback(() => {
    fetchQueue(); fetchAllAgents(); fetchStats();
  }, [fetchQueue, fetchAllAgents, fetchStats]);

  useEffect(() => { refresh(); setLoading(false); }, [refresh]);

  const handleStartReview = async (federationId) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/review/${federationId}/start`, { method: 'POST' });
      if (res.ok) { toast.success('Review started'); refresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to start review'); }
  };

  const handleApprove = async (federationId, trustClass, notes) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/review/${federationId}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trust_class: trustClass, notes }),
      });
      if (res.ok) { toast.success('Agent approved'); setSelectedAgent(null); refresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Approval failed'); }
    } catch { toast.error('Approval failed'); }
  };

  const handleReject = async (federationId, notes) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/review/${federationId}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (res.ok) { toast.success('Agent rejected'); setSelectedAgent(null); refresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Rejection failed'); }
    } catch { toast.error('Rejection failed'); }
  };

  const handleActivate = async (federationId) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/review/${federationId}/activate`, { method: 'POST' });
      if (res.ok) { toast.success('Agent activated — now available for routing'); setSelectedAgent(null); refresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Activation failed'); }
    } catch { toast.error('Activation failed'); }
  };

  const handleSuspend = async (federationId, reason) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/review/${federationId}/suspend`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) { toast.success('Agent suspended'); setSelectedAgent(null); refresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Suspension failed'); }
    } catch { toast.error('Suspension failed'); }
  };

  const currentList = activeTab === 'pending' ? queue : allAgents;
  const filtered = currentList.filter((a) => {
    if (statusFilter && a.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.name || '').toLowerCase().includes(q) ||
             (a.developer_name || '').toLowerCase().includes(q) ||
             (a.canonical_capabilities || []).some((c) => c.toLowerCase().includes(q));
    }
    return true;
  });

  const tabs = [
    { id: 'pending', label: 'Pending Review', count: stats.pending_review || 0 },
    { id: 'all', label: 'All External Agents', count: stats.total || 0 },
  ];

  return (
    <div data-testid="external-agent-review" className="min-h-screen bg-[var(--surface-0)] px-6 py-8 lg:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            External Agent Review
          </h1>
          <p className="text-sm text-[#718096] mt-1">Review, approve, and govern external agent submissions</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mb-6">
          {[
            { label: 'Pending', value: stats.pending_review, color: 'yellow' },
            { label: 'Draft', value: stats.draft, color: 'gray' },
            { label: 'Approved', value: stats.approved, color: 'cyan' },
            { label: 'Active', value: stats.active, color: 'emerald' },
            { label: 'Rejected', value: stats.rejected, color: 'red' },
            { label: 'Suspended', value: stats.suspended, color: 'orange' },
            { label: 'Total', value: stats.total, color: 'white' },
          ].map((s) => (
            <div key={s.label} className="p-3 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] text-center">
              <p className={`text-lg font-semibold text-${s.color === 'white' ? 'white' : s.color + '-400'}`}>{s.value ?? 0}</p>
              <p className="text-[10px] text-[#718096] uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-[var(--border-subtle)]">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`review-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'text-white border-white'
                  : 'text-[#718096] border-transparent hover:text-[#A0AEC0]'
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span className="ml-1.5 text-xs bg-[var(--surface-2)] px-1.5 py-0.5 rounded">{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
            <input
              data-testid="review-search"
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white placeholder-[#718096] focus:outline-none"
            />
          </div>
          {activeTab === 'all' && (
            <select
              data-testid="review-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white appearance-none cursor-pointer"
            >
              <option value="">All Status</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={24} className="animate-spin text-[#718096]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-subtle)]">
            <Globe size={36} className="mx-auto text-[#5C6778] mb-2" />
            <p className="text-sm text-[#718096]">
              {activeTab === 'pending' ? 'No agents pending review' : 'No external agents found'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((agent) => (
              <ReviewRow
                key={agent.federation_id}
                agent={agent}
                onSelect={() => setSelectedAgent(agent)}
                onStartReview={() => handleStartReview(agent.federation_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Review Detail Panel */}
      {selectedAgent && (
        <ReviewDetailPanel
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onActivate={handleActivate}
          onSuspend={handleSuspend}
        />
      )}
    </div>
  );
}

function ReviewRow({ agent, onSelect, onStartReview }) {
  const sc = STATUS_CONFIG[agent.status] || STATUS_CONFIG.submitted;
  const StatusIcon = sc.icon;
  const health = HEALTH_STYLES[agent.health_status] || HEALTH_STYLES.unknown;
  const caps = agent.canonical_capabilities || [];

  return (
    <div
      data-testid={`review-row-${agent.federation_id}`}
      className="p-4 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] hover:border-[rgba(255,255,255,0.12)] transition-all cursor-pointer flex items-center gap-4"
      onClick={onSelect}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-semibold text-white truncate">{agent.name}</h3>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${sc.bg} ${sc.text} ${sc.border} flex items-center gap-0.5`}>
            <StatusIcon size={10} /> {sc.label}
          </span>
          <Heartbeat size={12} className={health} />
        </div>
        <div className="flex items-center gap-3 text-xs text-[#718096]">
          {agent.developer_name && <span>{agent.developer_name}</span>}
          <span>Auth: {AUTH_LABELS[agent.auth_method] || agent.auth_method}</span>
          <span>Trust: {agent.trust_class}</span>
          {agent.submitted_at && <span>Submitted: {new Date(agent.submitted_at).toLocaleDateString()}</span>}
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {caps.slice(0, 6).map((c) => (
            <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] border border-[var(--border-subtle)] text-[#A0AEC0]">{c}</span>
          ))}
          {caps.length > 6 && <span className="text-[10px] text-[#718096]">+{caps.length - 6}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {agent.status === 'submitted' && (
          <button
            data-testid={`start-review-${agent.federation_id}`}
            onClick={(e) => { e.stopPropagation(); onStartReview(); }}
            className="px-3 py-1.5 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-500/25 transition-colors"
          >
            <Eye size={12} className="inline mr-1" /> Start Review
          </button>
        )}
        <ArrowRight size={16} className="text-[#5C6778]" />
      </div>
    </div>
  );
}

function ReviewDetailPanel({ agent, onClose, onApprove, onReject, onActivate, onSuspend }) {
  const [trustClass, setTrustClass] = useState(agent.trust_class || 'external_registered');
  const [notes, setNotes] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const sc = STATUS_CONFIG[agent.status] || STATUS_CONFIG.submitted;
  const StatusIcon = sc.icon;
  const health = HEALTH_STYLES[agent.health_status] || HEALTH_STYLES.unknown;
  const caps = agent.canonical_capabilities || [];
  const contract = agent.contract || {};
  const invoke = contract.invoke || {};

  const canApprove = ['submitted', 'in_review'].includes(agent.status);
  const canReject = ['submitted', 'in_review'].includes(agent.status);
  const canActivate = agent.status === 'approved';
  const canSuspend = ['active', 'approved'].includes(agent.status);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="review-detail-panel" className="relative w-full max-w-xl bg-[var(--surface-1)] border-l border-[var(--border-subtle)] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--surface-1)] border-b border-[var(--border-subtle)] px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-white">{agent.name}</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.1)] text-[#718096]">
              <span className="text-lg">&times;</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded border ${sc.bg} ${sc.text} ${sc.border} flex items-center gap-1`}>
              <StatusIcon size={12} /> {sc.label}
            </span>
            <span className={`text-xs ${health}`}>
              <Heartbeat size={12} className="inline mr-0.5" />
              {agent.health_status || 'unknown'}
            </span>
            {agent.developer_name && <span className="text-xs text-[#718096]">by {agent.developer_name}</span>}
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Description */}
          {agent.description && (
            <DetailSection title="Description">
              <p className="text-sm text-[#A0AEC0]">{agent.description}</p>
            </DetailSection>
          )}

          {/* Endpoint Inspection */}
          <DetailSection title="Endpoint">
            <div className="text-xs font-mono bg-[var(--surface-2)] p-2 rounded border border-[var(--border-subtle)] text-[#A0AEC0] break-all">
              {invoke.method || 'POST'} {invoke.endpoint_url || '—'}
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-[#718096]">
              <span>Timeout: {invoke.timeout_ms || '30000'}ms</span>
              <span>Auth: {AUTH_LABELS[agent.auth_method] || agent.auth_method}</span>
            </div>
          </DetailSection>

          {/* Capability Inspection */}
          <DetailSection title={`Capabilities (${caps.length})`}>
            <div className="flex flex-wrap gap-1.5">
              {caps.map((c) => (
                <span key={c} className="text-xs px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300">
                  <Lightning size={10} className="inline mr-1" /> {c}
                </span>
              ))}
            </div>
          </DetailSection>

          {/* Schema Inspection */}
          {(invoke.input_schema && Object.keys(invoke.input_schema).length > 0) && (
            <DetailSection title="Input Schema">
              <pre className="text-xs font-mono bg-[var(--surface-2)] p-3 rounded border border-[var(--border-subtle)] text-[#A0AEC0] overflow-x-auto max-h-32">
                {JSON.stringify(invoke.input_schema, null, 2)}
              </pre>
            </DetailSection>
          )}
          {(invoke.output_schema && Object.keys(invoke.output_schema).length > 0) && (
            <DetailSection title="Output Schema">
              <pre className="text-xs font-mono bg-[var(--surface-2)] p-3 rounded border border-[var(--border-subtle)] text-[#A0AEC0] overflow-x-auto max-h-32">
                {JSON.stringify(invoke.output_schema, null, 2)}
              </pre>
            </DetailSection>
          )}

          {/* Health Check */}
          {contract.health?.endpoint_url && (
            <DetailSection title="Health Endpoint">
              <p className="text-xs font-mono text-[#A0AEC0]">{contract.health.endpoint_url}</p>
            </DetailSection>
          )}

          {/* Audit Log */}
          {agent.audit_log?.length > 0 && (
            <DetailSection title="Activity Log">
              <div className="space-y-1.5">
                {agent.audit_log.slice(-8).reverse().map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-[#718096]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5C6778] mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-[#A0AEC0] font-medium">{e.action}</span>
                      <span className="ml-2">{new Date(e.timestamp).toLocaleString()}</span>
                      {e.notes && <p className="text-[#5C6778] mt-0.5">{e.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {/* Review Actions */}
          {(canApprove || canReject || canActivate || canSuspend) && (
            <div className="border-t border-[var(--border-subtle)] pt-5 space-y-4">
              <h4 className="text-xs font-medium text-[#718096] uppercase tracking-wider">Admin Actions</h4>

              {/* Trust Class Assignment */}
              {canApprove && (
                <div>
                  <label className="text-xs text-[#718096] mb-1.5 block">Assign Trust Class</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TRUST_OPTIONS.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => setTrustClass(t.value)}
                        className={`p-2 rounded-lg border text-left transition-colors ${
                          trustClass === t.value
                            ? 'bg-blue-500/15 border-blue-500/40 text-blue-300'
                            : 'bg-[var(--surface-2)] border-[var(--border-subtle)] text-[#718096] hover:border-[rgba(255,255,255,0.15)]'
                        }`}
                      >
                        <p className="text-xs font-medium">{t.label}</p>
                        <p className="text-[10px] opacity-60">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Review Notes */}
              {(canApprove || canReject) && (
                <div>
                  <label className="text-xs text-[#718096] mb-1 block">Review Notes</label>
                  <textarea
                    data-testid="review-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Add review notes..."
                    className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] text-sm text-white placeholder-[#718096] resize-none"
                  />
                </div>
              )}

              {/* Approve / Reject */}
              {(canApprove || canReject) && (
                <div className="flex items-center gap-3">
                  {canApprove && (
                    <button
                      data-testid="approve-agent-btn"
                      onClick={() => onApprove(agent.federation_id, trustClass, notes)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-medium hover:bg-emerald-500/25 transition-colors"
                    >
                      <CheckCircle size={16} weight="fill" /> Approve
                    </button>
                  )}
                  {canReject && (
                    <button
                      data-testid="reject-agent-btn"
                      onClick={() => onReject(agent.federation_id, notes)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/25 transition-colors"
                    >
                      <XCircle size={16} weight="fill" /> Reject
                    </button>
                  )}
                </div>
              )}

              {/* Activate */}
              {canActivate && (
                <button
                  data-testid="activate-agent-btn"
                  onClick={() => onActivate(agent.federation_id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-medium hover:bg-emerald-500/25 transition-colors"
                >
                  <Play size={16} weight="fill" /> Activate for Routing
                </button>
              )}

              {/* Suspend */}
              {canSuspend && (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    placeholder="Suspension reason..."
                    className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] text-sm text-white placeholder-[#718096]"
                  />
                  <button
                    data-testid="suspend-agent-btn"
                    onClick={() => onSuspend(agent.federation_id, suspendReason)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-500/15 border border-orange-500/30 text-orange-400 text-sm font-medium hover:bg-orange-500/25 transition-colors"
                  >
                    <Pause size={16} weight="fill" /> Suspend
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-medium text-[#718096] uppercase tracking-wider mb-2">{title}</h4>
      {children}
    </div>
  );
}

export default ExternalAgentReview;
